<template>
  <section class="form-fw__content">
    <form
      v-if="status !== isFailed"
      class="form--custom"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="form__container"
        :disabled="status === isLoading"
      >
        <div class="form__container__content">
          <div class="cursor--not-allowed">
            <label>{{ $t(`${namespace}.fieldDisplayNameLabel`) }}</label>
            <v-text-field
              v-model="form.displayName"
              :placeholder="$t(`${namespace}.fieldDisplayNamePlaceholder`)"
              :error-messages="errors.collect($t(`${namespace}.fieldDisplayNameName`))"
              :data-vv-name="$t(`${namespace}.fieldDisplayNameName`)"
              disabled
              required
              flat
              outline
              solo
              color="accent"
            />

            <v-layout row wrap>
              <v-flex md6>
                <div class="pr-3--desktop">
                  <label>{{ $t(`${namespace}.fieldCompanyLabel`) }}*</label>
                  <v-text-field
                    v-model="form.name"
                    :placeholder="$t(`${namespace}.fieldCompanyPlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldCompanyName`))"
                    v-validate="{ required: true }"
                    :data-vv-name="$t(`${namespace}.fieldCompanyName`)"
                    disabled
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>

              <v-flex md6>
                <div class="pl-3--desktop">
                  <label>{{ $t(`${namespace}.fieldDocumentIdLabel`) }}*</label>
                  <v-text-field
                    v-model="form.documentId"
                    :placeholder="$t(`${namespace}.fieldDocumentIdPlaceholder`)"
                    :error-messages="errors.collect($t(`${namespace}.fieldDocumentIdName`))"
                    v-validate="{
                      required: true,
                      cnpj: true
                    }"
                    mask="##.###.###/####-##"
                    :data-vv-name="$t(`${namespace}.fieldDocumentIdName`)"
                    disabled
                    required
                    flat
                    outline
                    solo
                    color="accent"
                  />
                </div>
              </v-flex>
            </v-layout>
          </div>

          <label>{{ $t(`${namespace}.fieldPhoneLabel`) }}*</label>
          <v-text-field
            v-model="phone"
            :placeholder="$t(`${namespace}.fieldPhonePlaceholder`)"
            :error-messages="errors.collect($t(`${namespace}.fieldPhoneName`))"
            v-validate="{ required: true, regex: /^\(\d{2}\)\s(\d{4,5})-(\d{4})$/ }"
            :data-vv-name="$t(`${namespace}.fieldPhoneName`)"
            required
            flat
            outline
            solo
            color="accent"
            maxlength="15"
            @input="setPhoneMask($event, 'phone')"
          />
        </div>
      </fieldset>
    </form>

    <div
      v-if="status === isFailed"
      class="text-xs-center mt-5"
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="$emit('tryAgain')"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </div>
  </section>
</template>

<script>
import { mapActions } from 'vuex'
import { isObject, isArray, objectToArray } from 'utils/functions'
import validator from 'utils/validator'
import { masks } from 'utils/masks'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { goBack } from '@/utils/functions'

export default {
  name: 'BasicForm',
  props: {
    kind: {
      type: String
    },
    data: {
      type: Object
    },
    submit: {
      type: Boolean
    },
    status: {
      type: Number
    }
  },
  computed: {
    isLoading: () => STATUS_IS_LOADING,
    isSuccess: () => STATUS_SUCCESS,
    isFailed: () => STATUS_FAILED
  },
  data () {
    return {
      namespace: 'Views.CompanyDataPage.form',
      isFetching: false,
      companyGroup: null,
      emailRegex: /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/,
      phone: null,
      form: {
        id: null,
        displayName: null,
        name: null,
        documentId: null,
        companyGroupId: null,
        postalCode: null,
        phone: null,
        address: null,
        district: null,
        state: null,
        city: null,
        contactEmailList: []
      }
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidObject: (val) => isObject(val),

    isValidArray: (val) => isArray(val),

    objectToArray: (val) => objectToArray(val),

    setPhoneMask (val, mask) {
      this.phone = masks[mask](val)
      this.form.phone = val.replace(/\D/g, '')
    },

    prevPage () {
      goBack()
    },

    async setData () {
      if (this.kind === 'editar' && this.isValidObject(this.data)) {
        this.isFetching = true

        const {
          id,
          displayName,
          name,
          documentId,
          companyGroupId = null,
          postalCode,
          phone,
          address,
          district,
          state,
          city,
          contactEmailList = []
        } = this.data

        this.form = {
          ...this.data,
          id,
          displayName,
          name,
          documentId: documentId.replace(/\D/g, ''),
          companyGroupId,
          postalCode: postalCode.replace(/\D/g, ''),
          phone: phone.replace(/\D/g, ''),
          address,
          district,
          state,
          city,
          contactEmailList: contactEmailList.split(/\s*,\s*/)
        }

        this.setPhoneMask(this.form.phone, 'phone')
      }
    },

    resetFields () {
      this.phone = null
      this.companyGroup = null
      this.form = {
        id: null,
        displayName: null,
        name: null,
        documentId: null,
        companyGroupId: null,
        postalCode: null,
        phone: null,
        address: null,
        district: null,
        state: null,
        city: null,
        contactEmailList: []
      }
    },

    errorHandler (err) {
      if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
        const errors = err.response.data.errors
        const keys = Object.keys(errors)

        if (this.isValidArray(keys)) {
          const key = keys[0]
          this.errorAlert(`${key}: ${errors[key][0]}`)
          return
        }

        this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
        return
      }

      this.errorAlert(this.$t(`${this.namespace}.editErrorMessage`))
    },

    redirectToCompanyTab () {
      this.$router
        .push({
          name: 'Company',
          query: { tab: 'company' }
        })
        .catch(() => {})
    },

    dispatchHeaderErrorMessage () {
      const { errors } = this.$validator
      const errorsLength = errors.items.length > 0
      this.$emit('headerErrorMessage', errorsLength)
    },

    async edit () {
      this.$emit('setCurrentStatus', this.isLoading)

      try {
        const { id, phone } = this.form
        await this.$apiService.company.updateCompanyPhone(id, { phone })
        this.successAlert(this.$t(`${this.namespace}.editSuccessMessage`))
        scrollTo(0, 0)
        this.redirectToCompanyTab()
        // this.$emit('tryAgain')
      } catch (err) {
        this.errorHandler(err)
        this.$emit('setCurrentStatus', this.isSuccess)
      }
    },

    async handleSubmit () {
      try {
        const isValid = await this.$validator.validateAll()
        this.dispatchHeaderErrorMessage()

        if (isValid) {
          this.edit()
        } else {
          const target = this.$validator.errors.items[0]
          if (target) {
            const elem = this.$el.querySelector(`[data-vv-name="${target.field}"]`)
            elem.scrollIntoView(false)
            elem.focus()
          }
        }
      } catch (err) {
        console.log('ERROR', err)
      }
    }
  },
  watch: {
    kind () {
      this.setData()
    },

    data: {
      deep: true,
      handler () {
        this.setData()
      }
    },

    submit () {
      this.handleSubmit()
    }
  },
  created () {
    this.$validator.extend('cnpj', (value) => validator.cnpj(value))
    this.setData()
  }
}
</script>
